/**
 * LeadingEmptyLineFilter - Filters leading empty lines from page breaks
 *
 * Extracted from EdgeDocsCanvas.V3.tsx as part of Phase 3 Step 6.
 *
 * When WASM breaks at a form feed (\f), it may create a "continuation" line with
 * zero visible content (just the newline after the form feed). These appear as
 * blank lines at the top of subsequent pages and need to be filtered out.
 *
 * ⚠️ CRITICAL: This filter keeps at least one line for grapheme indexing.
 * If we remove all lines, the page has no grapheme range and clicks will fail.
 */

/**
 * Base interface for line data that can be filtered
 * Uses optional properties with 'unknown' to accept any line type
 */
export interface FilterableLine {
  runs?: Array<{ glyphs?: unknown[] }>;
  width_fp?: number;
  ends_with_page_break?: boolean;
  ends_with_newline?: boolean;
  start_grapheme_index: number;
  end_grapheme_index: number;
}

/**
 * Result of filtering leading empty lines
 */
export interface LeadingEmptyLineFilterResult {
  /** Number of empty lines removed */
  removedCount: number;
}

/**
 * Options for the leading empty line filter
 */
export interface LeadingEmptyLineFilterOptions {
  /** Page index (filtering only applies to pages > 0) */
  pageIndex: number;
  /** Enable console logging */
  enableLogging?: boolean;
  /** Priority label for logging (e.g., "Priority 1", "Priority 2") */
  priorityLabel?: string;
}

/**
 * Checks if a line is considered "empty" for filtering purposes
 *
 * A line is considered "empty" if:
 * 1. It has no runs
 * 2. It has only one run with zero visible glyphs
 * 3. It has zero width
 * 4. It's a page break line with no content
 * 5. It's a newline-only line (1 grapheme, ends_with_newline=true)
 *
 * @param line - The line to check
 * @returns Object with empty status and reason flags
 */
export function isEmptyLine(line: FilterableLine): {
  isEmpty: boolean;
  hasNoRuns: boolean;
  isEmptyRun: boolean;
  isZeroWidth: boolean;
  isPageBreakLine: boolean;
  isNewlineOnlyLine: boolean;
  graphemeCount: number;
} {
  const hasNoRuns = !line.runs || line.runs.length === 0;
  const isEmptyRun = line.runs?.length === 1 && 
    (line.runs[0]?.glyphs?.length ?? 0) === 0;
  const isZeroWidth = (line.width_fp ?? 0) === 0;
  const isPageBreakLine = line.ends_with_page_break === true && isZeroWidth;
  
  // Detect newline-only lines (result of \f\n sequence)
  // These have only 1 grapheme (the newline) and end with newline
  const graphemeCount = (line.end_grapheme_index ?? 0) - (line.start_grapheme_index ?? 0);
  const isNewlineOnlyLine = graphemeCount <= 1 && line.ends_with_newline === true;

  const isEmpty = hasNoRuns || isEmptyRun || isZeroWidth || isPageBreakLine || isNewlineOnlyLine;

  return {
    isEmpty,
    hasNoRuns,
    isEmptyRun,
    isZeroWidth,
    isPageBreakLine,
    isNewlineOnlyLine,
    graphemeCount
  };
}

/**
 * Filters leading empty lines from a page's line array
 *
 * This filter is designed for pages > 0 where form feed sequences can
 * create empty leading lines. The filter preserves at least one line
 * to maintain grapheme indexing for click handling.
 *
 * NOTE: This function mutates the input array for efficiency.
 *
 * @param lines - Array of lines to filter (MUTATES the array)
 * @param options - Filter options
 * @returns Result with count of removed lines
 *
 * @example
 * ```typescript
 * const result = filterLeadingEmptyLines(pageLines, {
 *   pageIndex: 1,
 *   enableLogging: true,
 *   priorityLabel: 'Priority 1'
 * });
 *
 * console.log(`Removed ${result.removedCount} empty lines`);
 * ```
 */
export function filterLeadingEmptyLines<T extends FilterableLine>(
  lines: T[],
  options: LeadingEmptyLineFilterOptions
): LeadingEmptyLineFilterResult {
  const { pageIndex, enableLogging = false, priorityLabel = '' } = options;
  
  // Only filter on pages > 0 (page 0 shouldn't have form feed artifacts)
  // Also only filter if we have more than 1 line (keep at least one for grapheme indexing)
  if (pageIndex === 0 || lines.length <= 1) {
    return { removedCount: 0 };
  }

  const originalCount = lines.length;
  
  // Remove leading empty lines, but keep at least 1 line
  while (lines.length > 1) {
    const firstLine = lines[0];
    const { 
      isEmpty, 
      hasNoRuns, 
      isEmptyRun, 
      isZeroWidth, 
      isPageBreakLine, 
      isNewlineOnlyLine, 
      graphemeCount 
    } = isEmptyLine(firstLine);
    
    if (isEmpty) {
      lines.shift();
      if (enableLogging) {
        const labelSuffix = priorityLabel ? ` (${priorityLabel})` : '';
        console.log(`🔧 [LeadingEmptyLineFilter] Page ${pageIndex}: Filtered out leading empty/newline line${labelSuffix}`, {
          hasNoRuns,
          isEmptyRun,
          isZeroWidth,
          isPageBreakLine,
          isNewlineOnlyLine,
          graphemeCount,
          remainingLines: lines.length
        });
      }
    } else {
      break; // First line has content, stop filtering
    }
  }

  const removedCount = originalCount - lines.length;
  
  if (removedCount > 0 && enableLogging) {
    const labelSuffix = priorityLabel ? ` (${priorityLabel})` : '';
    console.log(`✅ [LeadingEmptyLineFilter] Page ${pageIndex}: Filtered ${removedCount} leading empty lines${labelSuffix}`);
  }

  return { removedCount };
}
